import React, { ComponentProps, useLayoutEffect, useRef, useState } from 'react';
import { Box, TextArea, Button, Pane, Flex, Text, MarkdownText } from '@fullstory/components';
import { FormattedMessage, useIntl } from 'react-intl';

interface DescriptionMarkdownPreviewProps
  extends Omit<ComponentProps<typeof MarkdownText>, 'text'>,
    Pick<ComponentProps<typeof TextArea>, 'id' | 'testId' | 'onChange' | 'onBlur'> {
  value: string;
  maxLength: number;
  preventNewline?: boolean;
  placeholder?: string; // Will use a default if none is provided.
  // Size of the textarea
  rows?: number;
  setSaveDisabled: (disabled: boolean) => void;
  // TODO(sarahgrace): Consider adding an option to make it resizable
}

// These keys are allowed even if the user has reached the character limit.
const KEY_CODES_ALWAYS_ALLOWED = [
  'Backspace',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Delete',
];

export const DescriptionMarkdownPreview: React.FC<DescriptionMarkdownPreviewProps> = ({
  testId = 'description-markdown-preview',
  value,
  maxLength,
  preventNewline,
  placeholder,
  rows,
  setSaveDisabled,
  ...rest
}) => {
  const intl = useIntl();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [height, setHeight] = useState(0);
  const [usedCharacters, setUsedCharacters] = useState(0);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      setHeight(textareaRef.current.clientHeight);
    }
  }, [textareaRef]);

  return (
    <>
      <Box testId={testId}>
        <Pane
          style={{ display: showPreview ? 'block' : 'none' }}
          height={height}
          border="standard"
          borderRadius={2}
        >
          <Box padding={[8, 12, 12, 12]} overflowY="auto" height="100%">
            <MarkdownText
              text={value}
              onCharacterCountChange={count => {
                setUsedCharacters(count);
                setSaveDisabled(count > maxLength);
              }}
              ignoreMultilineComponents
            />
          </Box>
        </Pane>
        <TextArea
          rows={rows}
          style={{ display: showPreview ? 'none' : 'block' }}
          ref={textareaRef}
          value={value}
          onKeyDown={e => {
            if (e.key == 'Enter' && preventNewline) {
              e.preventDefault();
            }
            if (usedCharacters < maxLength + 50) {
              // Have not reached the limit, so it's fine to type
              // Note: we include an extra 50 characters above the max length to allow
              // users to paste URLs, go back and remove something else to get it under, etc.
              // The count will show red at maxLength.
              return;
            }
            // User has hit character limit. We should allow backspace and other keys
            // but prevent entering more text.
            if (!KEY_CODES_ALWAYS_ALLOWED.some(key => key == e.key)) {
              // User has not entered one of the allowed keys.
              e.preventDefault();
            }
          }}
          placeholder={
            placeholder ||
            intl.formatMessage({
              defaultMessage: `Add a description... (optional)`,
              id: 'hcPqPd',
            })
          }
          {...rest}
        />
      </Box>
      <Box marginTop={4}>
        <Flex justifyContent="space-between">
          <Button
            size="x-small"
            iconLeft={showPreview ? 'eye-inactive' : 'eye-active'}
            variant="minimal"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview
              ? intl.formatMessage({ defaultMessage: 'Hide preview', id: 'pXNgqm' })
              : intl.formatMessage({ defaultMessage: 'Show preview', id: 'u+VXeI' })}
          </Button>
          <Text
            appearance="bodySmall"
            color={usedCharacters > maxLength ? 'destructive' : 'secondary'}
            testId="used-count"
          >
            <FormattedMessage
              defaultMessage="{used}/{total}"
              id="tghm3i"
              values={{
                used: usedCharacters,
                total: maxLength,
              }}
            />
          </Text>
        </Flex>
      </Box>
      {/* If needed, we could show an error message here when the count exceeds max */}
    </>
  );
};
